/**
 * Design tokens — semantic color layer over the core palette.
 *
 * Element colours now resolve from the central palette (core/palette.ts, BL-52,
 * the single "Farb-Pflegetabelle") — ELEMENT_COLORS / ELEMENT_RAMP are derived
 * views of TOWER_PALETTE, NOT a second source. Edit tower colours there.
 *
 * Import from here (not inline hex) for any color that isn't in core/constants.
 * The core palette re-exports are for convenience — single import point.
 */
import { COLORS } from "../core/constants.ts";
import { ENEMY_PALETTE, TOWER_PALETTE } from "../core/palette.ts";

// Re-export core palette for single-import convenience
export { COLORS };

/** Element-specific primary colors (tower core + particle base).
 *  Derived from TOWER_PALETTE[el].mid — the single-source identity hue. */
export const ELEMENT_COLORS = {
  fire: TOWER_PALETTE.fire!.mid,
  water: TOWER_PALETTE.water!.mid,
  lightning: TOWER_PALETTE.lightning!.mid,
  signal: TOWER_PALETTE.signal!.mid,
  kinetic: TOWER_PALETTE.kinetic!.mid,
  virus: TOWER_PALETTE.virus!.mid,
} as const;

/** Per-element emissive ramp — hot-spot core → identity mid → dark rim.
 *  The anchors BL-26 (§6.1) consumes; resolved from the central TOWER_PALETTE. */
export const ELEMENT_RAMP = {
  fire:      { core: TOWER_PALETTE.fire!.core,      mid: TOWER_PALETTE.fire!.mid,      rim: TOWER_PALETTE.fire!.rim },
  water:     { core: TOWER_PALETTE.water!.core,     mid: TOWER_PALETTE.water!.mid,     rim: TOWER_PALETTE.water!.rim },
  lightning: { core: TOWER_PALETTE.lightning!.core, mid: TOWER_PALETTE.lightning!.mid, rim: TOWER_PALETTE.lightning!.rim },
  signal:    { core: TOWER_PALETTE.signal!.core,    mid: TOWER_PALETTE.signal!.mid,    rim: TOWER_PALETTE.signal!.rim },
  kinetic:   { core: TOWER_PALETTE.kinetic!.core,   mid: TOWER_PALETTE.kinetic!.mid,   rim: TOWER_PALETTE.kinetic!.rim },
  virus:     { core: TOWER_PALETTE.virus!.core,     mid: TOWER_PALETTE.virus!.mid,     rim: TOWER_PALETTE.virus!.rim },
} as const;

/** Element particle color palettes (3-stop gradients per element).
 *  Track the re-graded element identities (BL-52) — tune alongside TOWER_PALETTE.
 *  S139 / BL-132: re-toned to the Glacier pastel-neon direction; each gradient
 *  still respects its identity's light-behavior pattern (lightning + kinetic
 *  snap to white; virus drifts toward the cool-plum "toxic edge"). */
export const ELEMENT_PARTICLES = {
  fire: ["#F4C0BE", "#E89A98", "#7A3D3C"], // pastel coral embers — light → mid → deep wine
  water: ["#90C8F0", "#BCDCF8", "#E0EEF8"], // pastel sky droplets — mid → light → near-white
  lightning: ["#E8D098", "#F0E0BC", "#FFFFFF"], // cool sand snapping to white
  signal: ["#A898E8", "#C4B8F0", "#E0D8F4"], // cool-violet broadcast pulses
  kinetic: ["#D0D8E8", "#E6EBF2", "#FFFFFF"], // pearl-pastel steel streaks, near-white
  virus: ["#98D8B0", "#BCE6CC", "#A088D8"], // cool mint → lighter → cool plum edge
} as const;

/** Per-epoch grade anchors for the global Green -> Amber -> Red narrative arc. */
export const EPOCH = {
  I: { primary: COLORS.SENTINEL_GREEN, accent: COLORS.SENTINEL_GREEN },
  II: { primary: COLORS.SENTINEL_GREEN, accent: COLORS.AMBER_WARN },
  III: { primary: COLORS.ORACLE_RED, accent: COLORS.MALWARE_RED },
} as const;

/** ECHELON — the antagonist's presentation identity (S126 / BL-112 / D-224).
 *  Derived views of the central threat family (core/palette.ts §2): magenta is
 *  HER voice, distinct from ORACLE_RED (your mainframe's own alarm state).
 *  Every corruption layer (claimed cells, sigil, veil, tears, mainframe taint)
 *  resolves its color here — never inline. */
export const ECHELON = {
  /** Her primary signal — claimed cells, the sigil, the taint. */
  SIGNAL: ENEMY_PALETTE.corrupted_node!,
  /** Her depth — veil outer stop, late-act veins. */
  DEEP: ENEMY_PALETTE.cipher_breaker!,
} as const;

/** UI panel tokens (backgrounds, borders).
 *  S139 / BL-132: re-toned to the Glacier panel family — cool dark backgrounds,
 *  mint-aqua borders (track the new SENTINEL_GREEN). */
/**
 * BOARD (BL-287) — the playfield surface's own inks, promoted here from inline
 * literals in `GridRenderer` / `GridAmbience`.
 *
 * The board is the largest surface on screen, so its ink is identity, not a
 * local shade — which is exactly the line `designTokens.test.ts` draws for what
 * belongs in a palette module. Promoting them shrinks the inline-hex count in
 * the same pass that promoted `PATH_GREEN` out of its call site, rather than
 * growing it.
 *
 * **`accent` and `accentHot` were re-hued in the same move, and that IS a visual
 * change — a deliberate one.** They sat at 164° and 162°, i.e. *inside* the
 * 152-169° band the two gameplay greens occupy (the conduit at 152°,
 * `SENTINEL_GREEN` at 169°). `GridAmbience`'s own comment claimed the accent was
 * "a notch off `SENTINEL_GREEN` so board activity can never be mistaken for a
 * gameplay signal" — but the separation it actually achieved was in value and
 * saturation, never in hue. And the grammar rhymed: a pulse head is a small
 * bright mint dot travelling a board line; a path packet is a small bright mint
 * dot travelling the conduit. Moving both to 188° buys the hue separation the
 * comment always claimed, at **identical saturation and lightness** — so the
 * brightness calibration measured against the pre-BL-286 board is untouched and
 * only the hue moves.
 *
 * `line` is deliberately NOT re-hued. It is a dim static lattice that rhymes
 * with nothing; shifting the biggest surface's cast to fix a confusion it does
 * not cause would trade the board's established identity for nothing.
 */
export const BOARD = {
  /** The lattice ink — one colour for the whole grid, hierarchy carried by alpha. */
  line: "#1A6349",
  /** The live layer's base accent (pulse trails, node halos). */
  accent: "#2E747F",
  /** The brighter head of a travelling pulse / the hard core of a node. */
  accentHot: "#5FB2BF",
} as const;

export const PANEL = {
  BG: "#0e1320",
  BG_ELEVATED: "#171d2e",
  BORDER: "#88E0D040",
  BORDER_SOLID: "#88E0D0",
} as const;
